import i18next from 'i18next';
import { util } from '../../common/utilities.js';
import '../../common/pano-viewer/panoUtilities.js';
import '../../common/utilitiesSidewalk.js';
/** @typedef {import('../navigation/Compass.js').Compass} Compass */
/** @typedef {import('../canvas/ContextMenu.js').ContextMenu} ContextMenu */
/** @typedef {import('../Main.js').MinimapUi} MinimapUi */
/** @typedef {import('../panorama/PanoManager.js').PanoManager} PanoManager */

/**
 * The tutorial's script: every state, what it says and does, and where it goes next.
 * @param {ContextMenu} contextMenu - Closed by the steps that move on from a label.
 * @param {Compass} compass - Whose message markup the walk step copies.
 * @param {PanoManager} panoManager - Turned by the steps that re-aim the view.
 * @param {MinimapUi} uiMinimap - Whose holder shows the tutorial's screenshot map.
 */
export function OnboardingStates(contextMenu, compass, panoManager, uiMinimap) {
  const panoId = 'tutorial';
  const afterWalkPanoId = 'afterWalkTutorial';
  const headingRanges = {
    'stage-1': [230, 233],
    'stage-2-adjust': [197, 242],
    'stage-2': [197, 209],
    'stage-3-adjust': [167, 197],
    'stage-3': [167, 187],
    'stage-4-adjust': [98, 187],
    'stage-4': [98, 108],
    'stage-5-adjust': [359, 108],
    'stage-5': [355, 1],
    'stage-6-adjust': [315, 1],
    'stage-6': [315, 343],
    'stage-7': [281, 14],
  };

  // Inline replica of the hover card's Delete button (.label-hover-card__delete-button) for tutorial messages
  // that ask the user to delete a misplaced label.
  const deleteIconHtml = `
    <span class="onboarding-delete-button">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
        stroke-linejoin="round" aria-hidden="true">
        <polyline points="3 6 5 6 21 6"></polyline>
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
        <line x1="10" y1="11" x2="10" y2="17"></line>
        <line x1="14" y1="11" x2="14" y2="17"></line>
      </svg>${i18next.t('center-ui.hover-card.delete')}</span>`;

  // Inline replica of a rating segment (.severity-button) for tutorial messages that ask for a specific rating.
  // Mirrors the real segment's smiley icon and localized level name so the copy can't drift from the buttons on
  // screen (positive types read Good/Okay/Bad, negative ones Low/Medium/High).
  const ratingChipHtml = (labelType, severity) => {
    const levelName = i18next.t(`common:${util.misc.getRatingLevelKeys(labelType)[severity]}`);
    return `<span class="onboarding-rating-chip"><img
      src="${util.misc.getSmileyIconPath(severity, labelType, false)}" alt="">${levelName}</span>`;
  };

  // Inline replica of a tag pill for tutorial messages that name a specific tag, reusing the localized text the
  // real pills render (shortcut-letter underline included) so the copy can't drift from the pill on screen.
  const tagPillHtml = (labelType, tag) => {
    const text = util.misc.getLabelDescriptions(labelType).tagInfo[tag].text;
    return `<span class="tag-pill"><span class="tag-pill__label">${text}</span></span>`;
  };

  // Inline keycap for teaching keyboard shortcuts (e.g. press C for Curb Ramp).
  const kbdHtml = (key) => `<kbd class="onboarding-kbd">${key}</kbd>`;

  // Annotation and panoAnchor x/y below are angles expressed in tutorial-pano pixel units, not rows/columns of the
  // tutorial image: x runs east from true north (TUTORIAL_PANO_WIDTH spans 360°) and y from the horizon, positive
  // up (TUTORIAL_PANO_HEIGHT / 2 spans 90°) — hence the small negative y values, slightly below eye level. They are
  // decoded by util.pano.horizonRelativeCoordToPov, which is NOT the inverse of povToPanoCoord (#4957).
  this.states = [
    {
      // The welcome/skip UI now lives in the pre-tutorial intro (TutorialIntro), so this first state only positions
      // the pano at the tutorial's opening POV and immediately advances to the first labeling step.
      id: 'initialize',
      progression: true,
      properties: {
        action: 'Introduction',
        heading: 230,
        pitch: -6,
        zoom: 1,
      },
      panoId,
      annotations: null,
      transition: 'introduce-curb-ramp',
    },
    {
      id: 'introduce-curb-ramp',
      progression: true,
      properties: {
        action: 'Instruction',
        minHeading: headingRanges['stage-1'][0],
        maxHeading: headingRanges['stage-1'][1],
      },
      message: {
        message: i18next.t('tutorial.introduce-curb-ramp'),
        panoAnchor: { x: 9730, y: -350 },
      },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 9730,
          y: -350,
          length: 50,
          angle: 0,
          fill: 'yellow',
        },
      ],
      transition: 'select-label-type-1',
    },
    {
      id: 'select-label-type-1',
      progression: true,
      properties: {
        action: 'SelectLabelType',
        labelType: 'CurbRamp',
        minHeading: headingRanges['stage-1'][0],
        maxHeading: headingRanges['stage-1'][1],
      },
      message: { message: i18next.t('tutorial.select-label-type-1') },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 9730,
          y: -350,
          length: 50,
          angle: 0,
        },
      ],
      transition: 'label-attribute-1',
    },
    {
      id: 'label-attribute-1',
      progression: true,
      properties: [{
        action: 'LabelAccessibilityAttribute',
        labelNumber: 1,
        labelType: 'CurbRamp',
        imageX: 1170,
        imageY: 3800,
        tolerance: 300,
        minHeading: headingRanges['stage-1'][0],
        maxHeading: headingRanges['stage-1'][1],
      }],
      message: { message: i18next.t('tutorial.label-attribute-1') },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 9730,
          y: -350,
          length: 50,
          angle: 0,
          fill: 'yellow',
        },
      ],
      transition: [function (params) {
        if (params.accurate) {
          return 'rate-severity-1';
        } else {
          return 'delete-attribute-1';
        }
      }],
    },
    {
      id: 'delete-attribute-1',
      progression: false,
      properties: {
        action: 'DeleteAccessibilityAttribute',
        labelNumber: 1,
        labelType: 'CurbRamp',
        minHeading: headingRanges['stage-1'][0],
        maxHeading: headingRanges['stage-1'][1],
      },
      message: {
        message: `${i18next.t('tutorial.common.label-too-far-generic')} ${deleteIconHtml}`,
      },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 9730,
          y: -350,
          length: 50,
          angle: 0,
        },
      ],
      transition: 'redo-select-label-type-1',
    },
    {
      id: 'redo-select-label-type-1',
      progression: false,
      properties: {
        action: 'RedoSelectLabelType',
        labelNumber: 1,
        labelType: 'CurbRamp',
        minHeading: headingRanges['stage-1'][0],
        maxHeading: headingRanges['stage-1'][1],
      },
      message: { message: i18next.t('tutorial.common.re-label', { label_type: i18next.t('common:curb-ramp') }) },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 9730,
          y: -350,
          length: 50,
          angle: 0,
        },
      ],
      transition: 'label-attribute-1',
    },
    {
      id: 'rate-severity-1',
      progression: true,
      properties: {
        action: 'RateSeverity',
        labelNumber: 1,
        labelType: 'CurbRamp',
        minHeading: headingRanges['stage-1'][0],
        maxHeading: headingRanges['stage-1'][1],
      },
      message: {
        // escapeValue off here and at every sibling that interpolates a *Html helper: the value is a fragment this
        // file builds (ratingChipHtml / tagPillHtml / kbdHtml), so escaping it would print the span at the reader.
        message: i18next.t('tutorial.rate-severity-1', {
          rating: ratingChipHtml('CurbRamp', 2),
          interpolation: { escapeValue: false },
        }),
        anchor: '#severity-menu',
        placement: 'left',
      },
      panoId,
      annotations: null,
      transition() {
        const severity = parseInt(this.getAttribute('value'), 10);
        if (severity === 2) {
          return 'tag-attribute-1';
        } else {
          return 'redo-rate-attribute-1';
        }
      },
    },
    {
      id: 'redo-rate-attribute-1',
      progression: false,
      properties: {
        action: 'RedoRateSeverity',
        labelNumber: 1,
        labelType: 'CurbRamp',
        minHeading: headingRanges['stage-1'][0],
        maxHeading: headingRanges['stage-1'][1],
      },
      message: {
        message: i18next.t('tutorial.redo-rate-attribute-1', {
          rating: ratingChipHtml('CurbRamp', 2),
          interpolation: { escapeValue: false },
        }),
        anchor: '#severity-menu',
        placement: 'left',
      },
      panoId,
      annotations: null,
      transition() {
        const severity = parseInt(this.getAttribute('value'), 10);
        if (severity === 2) {
          return 'tag-attribute-1';
        } else {
          return 'redo-rate-attribute-1';
        }
      },
    },
    {
      id: 'tag-attribute-1',
      progression: true,
      properties: {
        action: 'AddTag',
        labelNumber: 1,
        labelType: 'CurbRamp',
        minHeading: headingRanges['stage-1'][0],
        maxHeading: headingRanges['stage-1'][1],
      },
      message: {
        message: i18next.t('tutorial.tag-attribute-1', {
          tag: tagPillHtml('CurbRamp', 'points into traffic'),
          interpolation: { escapeValue: false },
        }),
        anchor: '#context-menu-tag-section',
        placement: 'left',
      },
      panoId,
      annotations: null,
      transition() {
        const tags = this.getProperty('tagIds');
        if (tags.includes(2) && tags.length === 1) { // 2 is the id of the "points into traffic" tag.
          contextMenu.hide();
          return 'select-label-type-2';
        } else {
          return 'redo-tag-attribute-1';
        }
      },
    },
    {
      id: 'redo-tag-attribute-1',
      progression: false,
      properties: {
        action: 'RedoAddTag',
        labelNumber: 1,
        labelType: 'CurbRamp',
        minHeading: headingRanges['stage-1'][0],
        maxHeading: headingRanges['stage-1'][1],
      },
      message: {
        message: i18next.t('tutorial.redo-tag-attribute-1', {
          tag: tagPillHtml('CurbRamp', 'points into traffic'),
          interpolation: { escapeValue: false },
        }),
        anchor: '#context-menu-tag-section',
        placement: 'left',
      },
      panoId,
      annotations: null,
      transition() {
        const tags = this.getProperty('tagIds');
        if (tags.includes(2) && tags.length === 1) { // 2 is the id of the "points into traffic" tag.
          contextMenu.hide();
          return 'select-label-type-2';
        } else {
          return 'redo-tag-attribute-1';
        }
      },
    },
    {
      id: 'select-label-type-2',
      progression: true,
      properties: {
        action: 'SelectLabelType',
        labelNumber: 2,
        labelType: 'Crosswalk',
        minHeading: headingRanges['stage-1'][0],
        maxHeading: headingRanges['stage-1'][1],
      },
      message: { message: i18next.t('tutorial.select-label-type-2') },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 8950,
          y: -300,
          length: 50,
          angle: 0,
        },
      ],
      transition: 'label-attribute-2',
    },
    {
      id: 'label-attribute-2',
      progression: true,
      properties: [{
        action: 'LabelAccessibilityAttribute',
        labelNumber: 2,
        labelType: 'Crosswalk',
        imageX: 420,
        imageY: 3800,
        tolerance: 300,
        minHeading: headingRanges['stage-1'][0],
        maxHeading: headingRanges['stage-1'][1],
      }],
      message: { message: i18next.t('tutorial.label-attribute-2') },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 8950,
          y: -300,
          length: 50,
          angle: 0,
          fill: 'yellow',
        },
      ],
      transition: [function (params) {
        if (params.accurate) {
          return 'rate-severity-2';
        } else {
          return 'delete-attribute-2';
        }
      }],
    },
    {
      id: 'delete-attribute-2',
      progression: false,
      properties: {
        action: 'DeleteAccessibilityAttribute',
        labelNumber: 2,
        labelType: 'Crosswalk',
        minHeading: headingRanges['stage-1'][0],
        maxHeading: headingRanges['stage-1'][1],
      },
      message: {
        message: `${i18next.t('tutorial.common.label-too-far-crosswalk')} ${deleteIconHtml}`,
      },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 8950,
          y: -300,
          length: 50,
          angle: 0,
        },
      ],
      transition: 'redo-select-label-type-2',
    },
    {
      id: 'redo-select-label-type-2',
      progression: false,
      properties: {
        action: 'RedoSelectLabelType',
        labelNumber: 2,
        labelType: 'Crosswalk',
        minHeading: headingRanges['stage-1'][0],
        maxHeading: headingRanges['stage-1'][1],
      },
      message: { message: i18next.t('tutorial.common.re-label', { label_type: i18next.t('common:crosswalk') }) },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 8950,
          y: -300,
          length: 50,
          angle: 0,
        },
      ],
      transition: 'label-attribute-2',
    },
    {
      id: 'rate-severity-2',
      progression: true,
      properties: {
        action: 'RateSeverity',
        labelNumber: 2,
        labelType: 'Crosswalk',
        minHeading: headingRanges['stage-1'][0],
        maxHeading: headingRanges['stage-1'][1],
      },
      message: {
        message: i18next.t('tutorial.common.rate-crosswalk-sev-1', {
          rating: ratingChipHtml('Crosswalk', 1),
          interpolation: { escapeValue: false },
        }),
        anchor: '#severity-menu',
        placement: 'left',
      },
      panoId,
      annotations: null,
      transition() {
        const severity = parseInt(this.getAttribute('value'), 10);
        if (severity === 1) {
          contextMenu.hide();
          return 'adjust-heading-angle-1';
        } else {
          return 'redo-rate-attribute-2';
        }
      },
    },
    {
      id: 'redo-rate-attribute-2',
      progression: false,
      properties: {
        action: 'RedoRateSeverity',
        labelNumber: 2,
        labelType: 'Crosswalk',
        minHeading: headingRanges['stage-1'][0],
        maxHeading: headingRanges['stage-1'][1],
      },
      message: {
        message: i18next.t('tutorial.common.redo-rate-crosswalk-sev-1', {
          rating: ratingChipHtml('Crosswalk', 1),
          interpolation: { escapeValue: false },
        }),
        anchor: '#severity-menu',
        placement: 'left',
      },
      panoId,
      annotations: null,
      transition() {
        const severity = parseInt(this.getAttribute('value'), 10);
        if (severity === 1) {
          contextMenu.hide();
          return 'adjust-heading-angle-1';
        } else {
          return 'redo-rate-attribute-2';
        }
      },
    },
    {
      id: 'adjust-heading-angle-1',
      progression: true,
      properties: {
        action: 'AdjustHeadingAngle',
        heading: 210,
        tolerance: 20,
        minHeading: headingRanges['stage-2-adjust'][0],
        maxHeading: headingRanges['stage-2-adjust'][1],
      },
      message: { message: i18next.t('tutorial.adjust-heading-angle-1') },
      panoId,
      annotations: null,
      transition: 'zoom-in',
    },
    {
      id: 'zoom-in',
      progression: true,
      properties: {
        action: 'Zoom',
        type: 'in',
        minHeading: headingRanges['stage-2'][0],
        maxHeading: headingRanges['stage-2'][1],
      },
      message: {
        message: i18next.t('tutorial.zoom-in'),
        anchor: '#zoom-in-button',
        placement: 'left',
      },
      panoId,
      annotations: null,
      transition: 'select-label-type-3',
    },
    {
      id: 'select-label-type-3',
      progression: true,
      properties: {
        action: 'SelectLabelType',
        labelNumber: 3,
        labelType: 'CurbRamp',
        minHeading: headingRanges['stage-2'][0],
        maxHeading: headingRanges['stage-2'][1],
      },
      message: { message: i18next.t('tutorial.select-label-type-3') },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 8180,
          y: -340,
          length: 50,
          angle: 0,
          fill: null,
        },
      ],
      transition: 'label-attribute-3',
    },
    {
      id: 'label-attribute-3',
      progression: true,
      properties: [{
        action: 'LabelAccessibilityAttribute',
        labelNumber: 3,
        labelType: 'CurbRamp',
        imageX: 12950,
        imageY: 3720,
        tolerance: 300,
        minHeading: headingRanges['stage-2'][0],
        maxHeading: headingRanges['stage-2'][1],
      }],
      message: { message: i18next.t('tutorial.label-attribute-3') },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 8180,
          y: -340,
          length: 50,
          angle: 0,
          fill: 'yellow',
        },
      ],
      transition: [function (params) {
        if (params.accurate) {
          return 'rate-severity-3';
        } else {
          return 'delete-attribute-3';
        }
      }],
    },
    {
      id: 'delete-attribute-3',
      progression: false,
      properties: {
        action: 'DeleteAccessibilityAttribute',
        labelNumber: 3,
        labelType: 'CurbRamp',
        minHeading: headingRanges['stage-2'][0],
        maxHeading: headingRanges['stage-2'][1],
      },
      message: {
        message: `${i18next.t('tutorial.common.label-too-far-generic')} ${deleteIconHtml}`,
      },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 8180,
          y: -340,
          length: 50,
          angle: 0,
          fill: null,
        },
      ],
      transition: 'redo-select-label-type-3',
    },
    {
      id: 'redo-select-label-type-3',
      progression: false,
      properties: {
        action: 'RedoSelectLabelType',
        labelNumber: 3,
        labelType: 'CurbRamp',
        minHeading: headingRanges['stage-2'][0],
        maxHeading: headingRanges['stage-2'][1],
      },
      message: { message: i18next.t('tutorial.common.re-label', { label_type: i18next.t('common:curb-ramp') }) },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 8180,
          y: -340,
          length: 50,
          angle: 0,
          fill: null,
        },
      ],
      transition: 'label-attribute-3',
    },
    {
      id: 'rate-severity-3',
      progression: true,
      properties: {
        action: 'RateSeverity',
        labelNumber: 3,
        labelType: 'CurbRamp',
        minHeading: headingRanges['stage-2'][0],
        maxHeading: headingRanges['stage-2'][1],
      },
      message: {
        message: i18next.t('tutorial.rate-severity-3', {
          rating: ratingChipHtml('CurbRamp', 2),
          interpolation: { escapeValue: false },
        }),
        anchor: '#severity-menu',
        placement: 'left',
      },
      panoId,
      annotations: null,
      transition() {
        const severity = parseInt(this.getAttribute('value'), 10);
        if (severity === 2) {
          return 'tag-attribute-3';
        } else {
          return 'redo-rate-attribute-3';
        }
      },
    },
    {
      id: 'redo-rate-attribute-3',
      progression: false,
      properties: {
        action: 'RedoRateSeverity',
        labelNumber: 3,
        labelType: 'CurbRamp',
        minHeading: headingRanges['stage-2'][0],
        maxHeading: headingRanges['stage-2'][1],
      },
      message: {
        message: i18next.t('tutorial.redo-rate-attribute-3', {
          rating: ratingChipHtml('CurbRamp', 2),
          interpolation: { escapeValue: false },
        }),
        anchor: '#severity-menu',
        placement: 'left',
      },
      panoId,
      annotations: null,
      transition() {
        const severity = parseInt(this.getAttribute('value'), 10);
        if (severity === 2) {
          return 'tag-attribute-3';
        } else {
          return 'redo-rate-attribute-3';
        }
      },
    },
    {
      id: 'tag-attribute-3',
      progression: true,
      properties: {
        action: 'AddTag',
        labelNumber: 3,
        labelType: 'CurbRamp',
        minHeading: headingRanges['stage-2'][0],
        maxHeading: headingRanges['stage-2'][1],
      },
      message: {
        message: i18next.t('tutorial.tag-attribute-3', {
          tag: tagPillHtml('CurbRamp', 'not enough landing space'),
          interpolation: { escapeValue: false },
        }),
        anchor: '#context-menu-tag-section',
        placement: 'left',
      },
      panoId,
      annotations: null,
      transition() {
        const tags = this.getProperty('tagIds');
        if (tags.includes(23) && tags.length === 1) { // 23 is the id of the "not enough landing space" tag.
          contextMenu.hide();
          return 'select-label-type-4';
        } else {
          return 'redo-tag-attribute-3';
        }
      },
    },
    {
      id: 'redo-tag-attribute-3',
      progression: false,
      properties: {
        action: 'RedoAddTag',
        labelNumber: 3,
        labelType: 'CurbRamp',
        minHeading: headingRanges['stage-2'][0],
        maxHeading: headingRanges['stage-2'][1],
      },
      message: {
        message: i18next.t('tutorial.redo-tag-attribute-3', {
          tag: tagPillHtml('CurbRamp', 'not enough landing space'),
          interpolation: { escapeValue: false },
        }),
        anchor: '#context-menu-tag-section',
        placement: 'left',
      },
      panoId,
      annotations: null,
      transition() {
        const tags = this.getProperty('tagIds');
        if (tags.includes(23) && tags.length === 1) { // 23 is the id of the "not enough landing space" tag.
          contextMenu.hide();
          return 'select-label-type-4';
        } else {
          return 'redo-tag-attribute-3';
        }
      },
    },
    {
      id: 'select-label-type-4',
      progression: true,
      properties: {
        action: 'SelectLabelType',
        labelNumber: 4,
        labelType: 'NoCurbRamp',
        minHeading: headingRanges['stage-2'][0],
        maxHeading: headingRanges['stage-2'][1],
      },
      message: { message: i18next.t('tutorial.select-label-type-4') },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 7800,
          y: -340,
          length: 50,
          angle: 0,
          fill: null,
        },
      ],
      transition: 'label-attribute-4',
    },
    {
      id: 'label-attribute-4',
      progression: true,
      properties: [{
        action: 'LabelAccessibilityAttribute',
        labelNumber: 4,
        labelType: 'NoCurbRamp',
        imageX: 12560,
        imageY: 3720,
        tolerance: 300,
        minHeading: headingRanges['stage-2'][0],
        maxHeading: headingRanges['stage-2'][1],
      }],
      message: { message: i18next.t('tutorial.label-attribute-4') },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 7800,
          y: -340,
          length: 50,
          angle: 0,
          fill: 'yellow',
        },
      ],
      transition: [function (params) {
        if (params.accurate) {
          return 'rate-severity-4';
        } else {
          return 'delete-attribute-4';
        }
      }],
    },
    {
      id: 'delete-attribute-4',
      progression: false,
      properties: {
        action: 'DeleteAccessibilityAttribute',
        labelNumber: 4,
        labelType: 'NoCurbRamp',
        minHeading: headingRanges['stage-2'][0],
        maxHeading: headingRanges['stage-2'][1],
      },
      message: {
        message: `${i18next.t('tutorial.common.label-too-far-generic')} ${deleteIconHtml}`,
      },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 7800,
          y: -340,
          length: 50,
          angle: 0,
          fill: null,
        },
      ],
      transition: 'redo-select-label-type-4',
    },
    {
      id: 'redo-select-label-type-4',
      progression: false,
      properties: {
        action: 'RedoSelectLabelType',
        labelNumber: 4,
        labelType: 'NoCurbRamp',
        minHeading: headingRanges['stage-2'][0],
        maxHeading: headingRanges['stage-2'][1],
      },
      message: { message: i18next.t('tutorial.common.re-label', { label_type: i18next.t('common:no-curb-ramp') }) },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 7800,
          y: -340,
          length: 50,
          angle: 0,
          fill: null,
        },
      ],
      transition: 'label-attribute-4',
    },
    {
      id: 'rate-severity-4',
      progression: true,
      properties: {
        action: 'RateSeverity',
        labelNumber: 4,
        labelType: 'NoCurbRamp',
        minHeading: headingRanges['stage-2'][0],
        maxHeading: headingRanges['stage-2'][1],
      },
      message: {
        message: i18next.t('tutorial.rate-severity-4', {
          rating1: ratingChipHtml('NoCurbRamp', 2),
          rating2: ratingChipHtml('NoCurbRamp', 3),
          interpolation: { escapeValue: false },
        }),
        anchor: '#severity-menu',
        placement: 'left',
      },
      panoId,
      annotations: null,
      transition() {
        const severity = parseInt(this.getAttribute('value'), 10);
        if (severity === 2 || severity === 3) {
          return 'tag-attribute-4';
        } else {
          return 'redo-rate-attribute-4';
        }
      },
    },
    {
      id: 'redo-rate-attribute-4',
      progression: false,
      properties: {
        action: 'RedoRateSeverity',
        labelNumber: 4,
        labelType: 'NoCurbRamp',
        minHeading: headingRanges['stage-2'][0],
        maxHeading: headingRanges['stage-2'][1],
      },
      message: {
        message: i18next.t('tutorial.redo-rate-attribute-4', {
          rating1: ratingChipHtml('NoCurbRamp', 2),
          rating2: ratingChipHtml('NoCurbRamp', 3),
          interpolation: { escapeValue: false },
        }),
        anchor: '#severity-menu',
        placement: 'left',
      },
      panoId,
      annotations: null,
      transition() {
        const severity = parseInt(this.getAttribute('value'), 10);
        if (severity === 2 || severity === 3) {
          return 'tag-attribute-4';
        } else {
          return 'redo-rate-attribute-4';
        }
      },
    },
    {
      id: 'tag-attribute-4',
      progression: true,
      properties: {
        action: 'AddTag',
        labelNumber: 4,
        labelType: 'NoCurbRamp',
        minHeading: headingRanges['stage-2'][0],
        maxHeading: headingRanges['stage-2'][1],
      },
      message: {
        message: i18next.t('tutorial.tag-attribute-4', {
          tag: tagPillHtml('NoCurbRamp', 'alternate route present'),
          interpolation: { escapeValue: false },
        }),
        anchor: '#context-menu-tag-section',
        placement: 'left',
      },
      panoId,
      annotations: null,
      transition() {
        const tags = this.getProperty('tagIds');
        if (tags.includes(5) && tags.length === 1) { // 5 is the id of the "alternate route present" tag.
          contextMenu.hide();
          return 'select-label-type-5';
        } else {
          return 'redo-tag-attribute-4';
        }
      },
    },
    {
      id: 'redo-tag-attribute-4',
      progression: false,
      properties: {
        action: 'RedoAddTag',
        labelNumber: 4,
        labelType: 'NoCurbRamp',
        minHeading: headingRanges['stage-2'][0],
        maxHeading: headingRanges['stage-2'][1],
      },
      message: {
        message: i18next.t('tutorial.redo-tag-attribute-4', {
          tag: tagPillHtml('NoCurbRamp', 'alternate route present'),
          interpolation: { escapeValue: false },
        }),
        anchor: '#context-menu-tag-section',
        placement: 'left',
      },
      panoId,
      annotations: null,
      transition() {
        const tags = this.getProperty('tagIds');
        if (tags.includes(5) && tags.length === 1) { // 5 is the id of the "alternate route present" tag.
          contextMenu.hide();
          return 'select-label-type-5';
        } else {
          return 'redo-tag-attribute-4';
        }
      },
    },
    {
      id: 'select-label-type-5',
      progression: true,
      properties: {
        action: 'SelectLabelType',
        labelNumber: 5,
        labelType: 'Signal',
        minHeading: headingRanges['stage-2'][0],
        maxHeading: headingRanges['stage-2'][1],
      },
      message: { message: i18next.t('tutorial.select-label-type-5') },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 7705,
          y: -285,
          length: 50,
          angle: 0,
        },
        {
          type: 'box',
          x: 7735,
          y: 64,
          width: 30,
          height: 46,
        },
      ],
      transition: 'label-attribute-5',
    },
    {
      id: 'label-attribute-5',
      progression: true,
      properties: [{
        action: 'LabelAccessibilityAttribute',
        labelNumber: 5,
        labelType: 'Signal',
        imageX: 12500,
        imageY: 3690,
        tolerance: 200,
        minHeading: headingRanges['stage-2'][0],
        maxHeading: headingRanges['stage-2'][1],
      }],
      message: { message: i18next.t('tutorial.label-attribute-5') },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 7705,
          y: -285,
          length: 50,
          angle: 0,
          fill: 'yellow',
        },
        {
          type: 'box',
          x: 7735,
          y: 64,
          width: 30,
          height: 46,
        },
      ],
      transition: [function (params) {
        if (params.accurate) {
          contextMenu.hide();
          return 'zoom-out';
        } else {
          return 'delete-attribute-5';
        }
      }],
    },
    {
      id: 'delete-attribute-5',
      progression: false,
      properties: {
        action: 'DeleteAccessibilityAttribute',
        labelNumber: 5,
        labelType: 'Signal',
        minHeading: headingRanges['stage-2'][0],
        maxHeading: headingRanges['stage-2'][1],
      },
      message: {
        message: `${i18next.t('tutorial.common.label-too-far-signal')} ${deleteIconHtml}`,
      },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 7705,
          y: -285,
          length: 50,
          angle: 0,
          fill: null,
        },
        {
          type: 'box',
          x: 7735,
          y: 64,
          width: 30,
          height: 46,
        },
      ],
      transition: 'redo-select-label-type-5',
    },
    {
      id: 'redo-select-label-type-5',
      progression: false,
      properties: {
        action: 'RedoSelectLabelType',
        labelNumber: 5,
        labelType: 'Signal',
        minHeading: headingRanges['stage-2'][0],
        maxHeading: headingRanges['stage-2'][1],
      },
      message: { message: i18next.t('tutorial.common.re-label', { label_type: i18next.t('common:signal') }) },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 7705,
          y: -285,
          length: 50,
          angle: 0,
          fill: null,
        },
        {
          type: 'box',
          x: 7735,
          y: 64,
          width: 30,
          height: 46,
        },
      ],
      transition: 'label-attribute-5',
    },
    {
      id: 'zoom-out',
      progression: true,
      properties: {
        action: 'Zoom',
        type: 'out',
        minHeading: headingRanges['stage-2'][0],
        maxHeading: headingRanges['stage-2'][1],
      },
      message: {
        message: i18next.t('tutorial.zoom-out'),
        anchor: '#zoom-out-button',
        placement: 'left',
      },
      panoId,
      annotations: null,
      transition: 'adjust-heading-angle-2',
    },
    {
      id: 'adjust-heading-angle-2',
      progression: true,
      properties: {
        action: 'AdjustHeadingAngle',
        heading: 187,
        tolerance: 20,
        minHeading: headingRanges['stage-3-adjust'][0],
        maxHeading: headingRanges['stage-3-adjust'][1],
      },
      message: { message: i18next.t('tutorial.adjust-heading-angle-2') },
      panoId,
      annotations: null,
      transition: 'select-label-type-6',
    },
    {
      id: 'select-label-type-6',
      progression: true,
      properties: {
        action: 'SelectLabelType',
        labelNumber: 6,
        labelType: 'Crosswalk',
        minHeading: headingRanges['stage-3'][0],
        maxHeading: headingRanges['stage-3'][1],
      },
      message: { message: i18next.t('tutorial.select-label-type-6') },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 7050,
          y: -450,
          length: 50,
          angle: 0,
        },
      ],
      transition: 'label-attribute-6',
    },
    {
      id: 'label-attribute-6',
      progression: true,
      properties: [{
        action: 'LabelAccessibilityAttribute',
        labelNumber: 6,
        labelType: 'Crosswalk',
        imageX: 11850,
        imageY: 3980,
        tolerance: 350,
        minHeading: headingRanges['stage-3'][0],
        maxHeading: headingRanges['stage-3'][1],
      }],
      message: { message: i18next.t('tutorial.label-attribute-6') },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 7050,
          y: -450,
          length: 50,
          angle: 0,
          fill: 'yellow',
        },
      ],
      transition: [function (params) {
        if (params.accurate) {
          return 'rate-severity-6';
        } else {
          return 'delete-attribute-6';
        }
      }],
    },
    {
      id: 'delete-attribute-6',
      progression: false,
      properties: {
        action: 'DeleteAccessibilityAttribute',
        labelNumber: 6,
        labelType: 'Crosswalk',
        minHeading: headingRanges['stage-3'][0],
        maxHeading: headingRanges['stage-3'][1],
      },
      message: {
        message: `${i18next.t('tutorial.common.label-too-far-crosswalk')} ${deleteIconHtml}`,
      },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 7050,
          y: -450,
          length: 50,
          angle: 0,
          fill: null,
        },
      ],
      transition: 'redo-select-label-type-6',
    },
    {
      id: 'redo-select-label-type-6',
      progression: false,
      properties: {
        action: 'RedoSelectLabelType',
        labelNumber: 6,
        labelType: 'Crosswalk',
        minHeading: headingRanges['stage-3'][0],
        maxHeading: headingRanges['stage-3'][1],
      },
      message: { message: i18next.t('tutorial.common.re-label', { label_type: i18next.t('common:crosswalk') }) },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 7050,
          y: -450,
          length: 50,
          angle: 0,
          fill: null,
        },
      ],
      transition: 'label-attribute-6',
    },
    {
      id: 'rate-severity-6',
      progression: true,
      properties: {
        action: 'RateSeverity',
        labelNumber: 6,
        labelType: 'Crosswalk',
        minHeading: headingRanges['stage-3'][0],
        maxHeading: headingRanges['stage-3'][1],
      },
      message: {
        message: i18next.t('tutorial.common.rate-crosswalk-sev-1', {
          rating: ratingChipHtml('Crosswalk', 1),
          interpolation: { escapeValue: false },
        }),
        anchor: '#severity-menu',
        placement: 'left',
      },
      panoId,
      annotations: null,
      transition() {
        const severity = parseInt(this.getAttribute('value'), 10);
        if (severity === 1) {
          contextMenu.hide();
          return 'adjust-heading-angle-3';
        } else {
          return 'redo-rate-attribute-6';
        }
      },
    },
    {
      id: 'redo-rate-attribute-6',
      progression: false,
      properties: {
        action: 'RedoRateSeverity',
        labelNumber: 6,
        labelType: 'Crosswalk',
        minHeading: headingRanges['stage-3'][0],
        maxHeading: headingRanges['stage-3'][1],
      },
      message: {
        message: i18next.t('tutorial.common.redo-rate-crosswalk-sev-1', {
          rating: ratingChipHtml('Crosswalk', 1),
          interpolation: { escapeValue: false },
        }),
        anchor: '#severity-menu',
        placement: 'left',
      },
      panoId,
      annotations: null,
      transition() {
        const severity = parseInt(this.getAttribute('value'), 10);
        if (severity === 1) {
          contextMenu.hide();
          return 'adjust-heading-angle-3';
        } else {
          return 'redo-rate-attribute-6';
        }
      },
    },
    {
      id: 'adjust-heading-angle-3',
      progression: true,
      properties: {
        action: 'AdjustHeadingAngle',
        heading: 115,
        tolerance: 20,
        minHeading: headingRanges['stage-4-adjust'][0],
        maxHeading: headingRanges['stage-4-adjust'][1],
      },
      message: {
        message: i18next.t('tutorial.adjust-heading-angle-3'),
        width: 190,
      },
      panoId,
      annotations: null,
      transition: 'speed-things-up',
    },
    {
      id: 'speed-things-up',
      progression: true,
      properties: {
        action: 'Instruction',
        minHeading: headingRanges['stage-4'][0],
        maxHeading: headingRanges['stage-4'][1],
      },
      message: { message: i18next.t('tutorial.speed-things-up') },
      panoId,
      annotations: [
        {
          type: 'label',
          labelType: 'CurbRamp',
          x: 3850,
          y: -975,
          lat: 38.94039365744705,
          lng: -77.06749504111177,
          keepUntil: 'select-label-type-8',
        },
        {
          type: 'label',
          labelType: 'CurbRamp',
          x: 4925,
          y: -850,
          lat: 38.940334177869914,
          lng: -77.06752911216995,
          keepUntil: 'select-label-type-8',
        },
        {
          type: 'label',
          labelType: 'Crosswalk',
          x: 2150,
          y: -1150,
          lat: 38.94048737098884,
          lng: -77.06751796530399,
          keepUntil: 'select-label-type-8',
        },
        {
          type: 'label',
          labelType: 'Signal',
          x: 5225,
          y: -650,
          lat: 38.94030890329519,
          lng: -77.06755034635032,
          keepUntil: 'select-label-type-8',
        },
      ],
      transition: 'select-label-type-7',
    },
    {
      id: 'select-label-type-7',
      progression: true,
      properties: {
        action: 'SelectLabelType',
        labelNumber: 7,
        labelType: 'NoSidewalk',
        minHeading: headingRanges['stage-4'][0],
        maxHeading: headingRanges['stage-4'][1],
      },
      message: { message: i18next.t('tutorial.select-label-type-7') },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 2776,
          y: -500,
          length: 50,
          angle: 0,
          fill: 'white',
        },
      ],
      transition: 'label-attribute-7',
    },
    {
      id: 'label-attribute-7',
      progression: true,
      properties: [{
        action: 'LabelAccessibilityAttribute',
        labelNumber: 7,
        labelType: 'NoSidewalk',
        imageX: 7550,
        imageY: 3900,
        tolerance: 300,
        minHeading: headingRanges['stage-4'][0],
        maxHeading: headingRanges['stage-4'][1],
      }],
      message: { message: i18next.t('tutorial.label-attribute-7') },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 2776,
          y: -500,
          length: 50,
          angle: 0,
          fill: 'yellow',
        },
      ],
      transition: [function (params) {
        if (params.accurate) {
          return 'tag-attribute-7';
        } else {
          return 'delete-attribute-7';
        }
      }],
    },
    {
      id: 'delete-attribute-7',
      progression: false,
      properties: {
        action: 'DeleteAccessibilityAttribute',
        labelNumber: 7,
        labelType: 'NoSidewalk',
        minHeading: headingRanges['stage-4'][0],
        maxHeading: headingRanges['stage-4'][1],
      },
      message: {
        message: `${i18next.t('tutorial.common.label-too-far-generic')} ${deleteIconHtml}`,
      },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 2776,
          y: -500,
          length: 50,
          angle: 0,
          fill: null,
        },
      ],
      transition: 'redo-select-label-type-7',
    },
    {
      id: 'redo-select-label-type-7',
      progression: false,
      properties: {
        action: 'RedoSelectLabelType',
        labelNumber: 7,
        labelType: 'NoSidewalk',
        minHeading: headingRanges['stage-4'][0],
        maxHeading: headingRanges['stage-4'][1],
      },
      message: { message: i18next.t('tutorial.common.re-label', { label_type: i18next.t('no-sidewalk') }) },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 2776,
          y: -500,
          length: 50,
          angle: 0,
          fill: null,
        },
      ],
      transition: 'label-attribute-7',
    },
    {
      id: 'tag-attribute-7',
      progression: true,
      properties: {
        action: 'AddTag',
        labelNumber: 7,
        labelType: 'NoSidewalk',
        minHeading: headingRanges['stage-4'][0],
        maxHeading: headingRanges['stage-4'][1],
      },
      message: {
        message: i18next.t('tutorial.tag-attribute-7', {
          tag1: tagPillHtml('NoSidewalk', 'ends abruptly'),
          tag2: tagPillHtml('NoSidewalk', 'street has a sidewalk'),
          interpolation: { escapeValue: false },
        }),
        anchor: '#context-menu-tag-section',
        placement: 'left',
      },
      panoId,
      annotations: null,
      transition() {
        const tags = this.getProperty('tagIds');
        if (tags.length === 2 && tags.includes(20) && tags.includes(21)) {
          // We have both tags correct, so lets continue.
          contextMenu.hide();
          return 'adjust-heading-angle-4';
        } else if (tags.length === 1 && (tags.includes(20) || tags.includes(21))) {
          // We have one of the two tags so far, so stay in this state.
          return 'tag-attribute-7';
        } else {
          // A mistake was made, move to the redo state.
          return 'redo-tag-attribute-7';
        }
      },
    },
    {
      id: 'redo-tag-attribute-7',
      progression: false,
      properties: {
        action: 'RedoAddTag',
        labelNumber: 7,
        labelType: 'NoSidewalk',
        minHeading: headingRanges['stage-4'][0],
        maxHeading: headingRanges['stage-4'][1],
      },
      message: {
        message: i18next.t('tutorial.redo-tag-attribute-7', {
          tag1: tagPillHtml('NoSidewalk', 'ends abruptly'),
          tag2: tagPillHtml('NoSidewalk', 'street has a sidewalk'),
          interpolation: { escapeValue: false },
        }),
        anchor: '#context-menu-tag-section',
        placement: 'left',
      },
      panoId,
      annotations: null,
      transition() {
        const tags = this.getProperty('tagIds');
        if (tags.length === 2 && tags.includes(20) && tags.includes(21)) {
          // We have both tags correct, so let's continue.
          contextMenu.hide();
          return 'adjust-heading-angle-4';
        } else if (tags.includes(20) || tags.includes(21)) {
          // We have at least one of the two tags so far, but not both. Move progress bar, stay in this state.
          return 'redo-tag-attribute-7';
        } else {
          // We don't have any correct tags, don't move progress bar forward, stay in same state.
          return 'redo-tag-attribute-7';
        }
      },
    },
    {
      id: 'adjust-heading-angle-4',
      progression: true,
      properties: {
        action: 'AdjustHeadingAngle',
        heading: 0,
        tolerance: 20,
        minHeading: headingRanges['stage-5-adjust'][0],
        maxHeading: headingRanges['stage-5-adjust'][1],
      },
      message: { message: i18next.t('tutorial.adjust-heading-angle-4') },
      panoId,
      annotations: null,
      transition: 'select-label-type-8',
    },
    {
      id: 'select-label-type-8',
      progression: true,
      properties: {
        action: 'SelectLabelType',
        labelNumber: 8,
        labelType: 'CurbRamp',
        minHeading: headingRanges['stage-5'][0],
        maxHeading: headingRanges['stage-5'][1],
      },
      message: {
        message: i18next.t('tutorial.select-label-type-8', {
          key: kbdHtml('C'),
          interpolation: { escapeValue: false },
        }),
      },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 750,
          y: -670,
          length: 50,
          angle: 0,
          fill: 'white',
        },
      ],
      transition: 'label-attribute-8',
    },
    {
      id: 'label-attribute-8',
      progression: true,
      properties: [{
        action: 'LabelAccessibilityAttribute',
        labelNumber: 8,
        labelType: 'CurbRamp',
        imageX: 5550,
        imageY: 4080,
        tolerance: 250,
        minHeading: headingRanges['stage-5'][0],
        maxHeading: headingRanges['stage-5'][1],
      }],
      message: { message: i18next.t('tutorial.label-attribute-8') },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 750,
          y: -670,
          length: 50,
          angle: 0,
          fill: 'yellow',
        },
      ],
      transition: [function (params) {
        if (params.accurate) {
          return 'rate-severity-8';
        } else {
          return 'delete-attribute-8';
        }
      }],
    },
    {
      id: 'delete-attribute-8',
      progression: false,
      properties: {
        action: 'DeleteAccessibilityAttribute',
        labelNumber: 8,
        labelType: 'CurbRamp',
        minHeading: headingRanges['stage-5'][0],
        maxHeading: headingRanges['stage-5'][1],
      },
      message: {
        message: `${i18next.t('tutorial.common.label-too-far-generic')} ${deleteIconHtml}`,
      },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 750,
          y: -670,
          length: 50,
          angle: 0,
          fill: null,
        },
      ],
      transition: 'redo-select-label-type-8',
    },
    {
      id: 'redo-select-label-type-8',
      progression: false,
      properties: {
        action: 'RedoSelectLabelType',
        labelNumber: 8,
        labelType: 'CurbRamp',
        minHeading: headingRanges['stage-5'][0],
        maxHeading: headingRanges['stage-5'][1],
      },
      message: { message: i18next.t('tutorial.common.re-label', { label_type: i18next.t('common:curb-ramp') }) },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 750,
          y: -670,
          length: 50,
          angle: 0,
          fill: null,
        },
      ],
      transition: 'label-attribute-8',
    },
    {
      id: 'rate-severity-8',
      progression: true,
      properties: {
        action: 'RateSeverity',
        labelNumber: 8,
        labelType: 'CurbRamp',
        minHeading: headingRanges['stage-5'][0],
        maxHeading: headingRanges['stage-5'][1],
      },
      message: {
        message: i18next.t('tutorial.rate-severity-8', {
          rating: ratingChipHtml('CurbRamp', 1),
          interpolation: { escapeValue: false },
        }),
        anchor: '#severity-menu',
        placement: 'left',
      },
      panoId,
      annotations: null,
      transition() {
        const severity = parseInt(this.getAttribute('value'), 10);
        if (severity === 1) {
          contextMenu.hide();
          return 'select-label-type-9';
        } else {
          return 'redo-rate-attribute-8';
        }
      },
    },
    {
      id: 'redo-rate-attribute-8',
      progression: false,
      properties: {
        action: 'RedoRateSeverity',
        labelNumber: 8,
        labelType: 'CurbRamp',
        minHeading: headingRanges['stage-5'][0],
        maxHeading: headingRanges['stage-5'][1],
      },
      message: {
        message: i18next.t('tutorial.redo-rate-attribute-8', {
          rating: ratingChipHtml('CurbRamp', 1),
          interpolation: { escapeValue: false },
        }),
        anchor: '#severity-menu',
        placement: 'left',
      },
      panoId,
      annotations: null,
      transition() {
        const severity = parseInt(this.getAttribute('value'), 10);
        if (severity === 1) {
          contextMenu.hide();
          return 'select-label-type-9';
        } else {
          return 'redo-rate-attribute-8';
        }
      },
    },
    {
      id: 'select-label-type-9',
      progression: true,
      properties: {
        action: 'SelectLabelType',
        labelNumber: 9,
        labelType: 'Signal',
        minHeading: headingRanges['stage-5'][0],
        maxHeading: headingRanges['stage-5'][1],
      },
      message: {
        message: i18next.t('tutorial.select-label-type-9', {
          key: kbdHtml('P'),
          interpolation: { escapeValue: false },
        }),
      },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 225,
          y: -750,
          length: 50,
          angle: 0,
        },
        {
          type: 'box',
          x: 255,
          y: 440,
          width: 45,
          height: 65,
        },
      ],
      transition: 'label-attribute-9',
    },
    {
      id: 'label-attribute-9',
      progression: true,
      properties: [{
        action: 'LabelAccessibilityAttribute',
        labelNumber: 9,
        labelType: 'Signal',
        imageX: 5015,
        imageY: 4260,
        tolerance: 200,
        minHeading: headingRanges['stage-5'][0],
        maxHeading: headingRanges['stage-5'][1],
      }],
      message: { message: i18next.t('tutorial.label-attribute-9') },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 225,
          y: -750,
          length: 50,
          angle: 0,
          fill: 'yellow',
        },
        {
          type: 'box',
          x: 255,
          y: 440,
          width: 45,
          height: 65,
        },
      ],
      transition: [function (params) {
        if (params.accurate) {
          contextMenu.hide();
          return 'adjust-heading-angle-5';
        } else {
          return 'delete-attribute-9';
        }
      }],
    },
    {
      id: 'delete-attribute-9',
      progression: false,
      properties: {
        action: 'DeleteAccessibilityAttribute',
        labelNumber: 9,
        labelType: 'Signal',
        minHeading: headingRanges['stage-5'][0],
        maxHeading: headingRanges['stage-5'][1],
      },
      message: {
        message: `${i18next.t('tutorial.common.label-too-far-signal')} ${deleteIconHtml}`,
      },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 225,
          y: -750,
          length: 50,
          angle: 0,
          fill: null,
        },
        {
          type: 'box',
          x: 255,
          y: 440,
          width: 45,
          height: 65,
        },
      ],
      transition: 'redo-select-label-type-9',
    },
    {
      id: 'redo-select-label-type-9',
      progression: false,
      properties: {
        action: 'RedoSelectLabelType',
        labelNumber: 9,
        labelType: 'Signal',
        minHeading: headingRanges['stage-5'][0],
        maxHeading: headingRanges['stage-5'][1],
      },
      message: { message: i18next.t('tutorial.common.re-label', { label_type: i18next.t('common:signal') }) },
      panoId,
      annotations: [
        {
          type: 'arrow',
          x: 225,
          y: -750,
          length: 50,
          angle: 0,
          fill: null,
        },
        {
          type: 'box',
          x: 255,
          y: 440,
          width: 45,
          height: 65,
        },
      ],
      transition: 'label-attribute-9',
    },
    {
      id: 'adjust-heading-angle-5',
      progression: true,
      properties: {
        action: 'AdjustHeadingAngle',
        heading: 346,
        tolerance: 20,
        minHeading: headingRanges['stage-6-adjust'][0],
        maxHeading: headingRanges['stage-6-adjust'][1],
      },
      message: { message: i18next.t('tutorial.adjust-heading-angle-5') },
      panoId,
      annotations: null,
      transition: 'walk-1',
    },
    {
      id: 'walk-1',
      progression: true,
      properties: {
        action: 'Instruction',
        minHeading: headingRanges['stage-6'][0],
        maxHeading: headingRanges['stage-6'][1],
        blinks: ['minimap'],
      },
      message: {
        message: i18next.t('tutorial.walk-1'),
        anchor: '#minimap-holder',
        placement: 'top',
      },
      panoId,
      transition() {
        // Set Compass Message
        const uiCompassMessageHolder = compass.getCompassMessageHolder();
        const src = util.escapeHTML(compass.directionToImagePath('straight'));
        const image = `<img src='${src}' class='compass-turn-images' alt='Turn icon' />`;
        const message = `<div class='compass-message-small'>${i18next.t('center-ui.compass.unlabeled-problems')
        }</div>${image}<span class='compass-message-large'>${i18next.t('center-ui.compass.straight')}</span>`;
        uiCompassMessageHolder.message.innerHTML = message;
        compass.showMessage();
        return 'walk-2';
      },
    },
    {
      id: 'walk-2',
      progression: true,
      properties: {
        action: 'Instruction',
        blinks: ['compass'],
        minHeading: headingRanges['stage-6'][0],
        maxHeading: headingRanges['stage-6'][1],
      },
      message: {
        message: i18next.t('tutorial.walk-2'),
        anchor: '#compass-message-holder',
        placement: 'top',
      },
      panoId,
      annotations: null,
      transition: 'walk-3',
    },
    {
      id: 'walk-3',
      progression: true,
      properties: {
        action: 'WalkTowards',
        blinks: ['compass', 'movement-arrow'],
        panoId: afterWalkPanoId,
        minHeading: headingRanges['stage-6'][0],
        maxHeading: headingRanges['stage-6'][1],
      },
      message: { message: i18next.t('tutorial.walk-3') },
      panoId,
      transition() {
        panoManager.setPov({ heading: 329, pitch: 0, zoom: 1 });
        uiMinimap.holder.style.backgroundImage
          = `url('${util.assetPath('images/explore/onboarding/afterWalkTutorialMiniMap.jpg')}')`;
        return 'walk-4';
      },
    },
    {
      id: 'walk-4',
      progression: true,
      properties: {
        action: 'Instruction',
        stopBlinking: true,
        minHeading: headingRanges['stage-7'][0],
        maxHeading: headingRanges['stage-7'][1],
        blinks: ['minimap'],
      },
      message: {
        message: i18next.t('tutorial.walk-4'),
        width: 350,
        anchor: '#minimap-holder',
        placement: 'top',
      },
      panoId: afterWalkPanoId,
      annotations: null,
      transition: 'walk-5',
    },
    {
      id: 'walk-5',
      progression: true,
      properties: {
        action: 'Instruction',
        minHeading: headingRanges['stage-7'][0],
        maxHeading: headingRanges['stage-7'][1],
      },
      message: {
        message: i18next.t('tutorial.walk-5-1'),
        width: 400,
      },
      panoId: afterWalkPanoId,
      annotations: [
        {
          type: 'arrow',
          x: 200,
          y: -600,
          length: 50,
          angle: 0,
          fill: 'yellow',
        },
        {
          type: 'arrow',
          x: -2530,
          y: -470,
          length: 50,
          angle: 0,
          fill: 'yellow',
        },

      ],
      okButtonText: i18next.t('tutorial.walk-5-2'),
      transition: 'walk-6',
    },
    {
      id: 'walk-6',
      progression: true,
      properties: {
        action: 'Instruction',
        minHeading: headingRanges['stage-7'][0],
        maxHeading: headingRanges['stage-7'][1],
      },
      message: {
        message: i18next.t('tutorial.walk-6'),
        width: 400,
      },
      panoId: afterWalkPanoId,
      annotations: [
        {
          type: 'label',
          labelType: 'NoCurbRamp',
          x: 10800,
          y: -525,
          lat: 38.94067750463137,
          lng: -77.06786106607106,
          keepUntil: 'outro',
        },
        {
          type: 'label',
          labelType: 'NoCurbRamp',
          x: 225,
          y: -700,
          lat: 38.94076274068959,
          lng: -77.0676653183858,
          keepUntil: 'outro',
        },
        {
          type: 'label',
          labelType: 'Crosswalk',
          x: 12150,
          y: -785,
          lat: 38.940735726268095,
          lng: -77.06778530284552,
          keepUntil: 'outro',
        },
        {
          type: 'label',
          labelType: 'Signal',
          x: 10510,
          y: -500,
          lat: 38.940656819984454,
          lng: -77.06787178273665,
          keepUntil: 'outro',
        },
        {
          type: 'label',
          labelType: 'Signal',
          x: 510,
          y: -600,
          lat: 38.94076199025627,
          lng: -77.06763764329555,
          keepUntil: 'outro',
        },

      ],
      transition: 'instruction-1',
    },
    {
      id: 'instruction-1',
      progression: true,
      properties: {
        action: 'Instruction',
        blinks: ['minimap'],
        minHeading: headingRanges['stage-7'][0],
        maxHeading: headingRanges['stage-7'][1],
      },
      message: {
        message: i18next.t('tutorial.instruction-1'),
        anchor: '#minimap-holder',
        placement: 'top',
      },
      panoId: afterWalkPanoId,
      annotations: null,
      transition: 'instruction-2',
    },
    {
      id: 'instruction-2',
      progression: true,
      properties: {
        action: 'Instruction',
        blinks: ['stuck'],
        minHeading: headingRanges['stage-7'][0],
        maxHeading: headingRanges['stage-7'][1],
      },
      message: {
        message: i18next.t('tutorial.instruction-2'),
        anchor: '#explore-control-stuck',
        placement: 'right',
      },
      panoId: afterWalkPanoId,
      annotations: null,
      transition: 'outro',
    },
    {
      id: 'outro',
      progression: true,
      properties: {
        action: 'Instruction',
        heading: 280,
        pitch: -6,
        zoom: 1,
        minHeading: undefined,
        maxHeading: undefined,
      },
      message: {
        message() {
          return document.getElementById('onboarding-outro').innerHTML;
        },
        background: true,
        fullPage: true,
      },
      okButton: false,
      panoId: afterWalkPanoId,
      annotations: null,
      transition: 'end-onboarding',
    },
    {
      'id': 'end-onboarding',
      'progression': false,
      'end-onboarding': {
        skip: false,
      },
    },
  ];

  this.get = function () {
    return this.states;
  };
}
